import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faMicrophone, faRefresh, faCake, faUsers, faRocket } from '@fortawesome/free-solid-svg-icons';
import * as discordApi from '../services/discordApi';
import type { ServerStats, VoiceChannel, Birthday } from '../services/discordApi';

interface BotConfigProps {
  onBack: () => void;
}

function BotConfig({ onBack }: BotConfigProps) {
  const [serverStats, setServerStats] = useState<ServerStats | null>(null);
  const [voiceChannels, setVoiceChannels] = useState<VoiceChannel[]>([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<Birthday[]>([]);
  const [botHealth, setBotHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [birthdayCheckLoading, setBirthdayCheckLoading] = useState(false);
  const [birthdayCheckMessage, setBirthdayCheckMessage] = useState<string | null>(null);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [statsRes, voiceRes, birthdaysRes, healthRes] = await Promise.all([
        discordApi.getServerStats(),
        discordApi.getVoiceChannels(),
        discordApi.getUpcomingBirthdays(7),
        discordApi.getHealth(),
      ]);

      if (statsRes.success && statsRes.data) {
        setServerStats(statsRes.data);
      }

      if (voiceRes.success && voiceRes.data) {
        setVoiceChannels(voiceRes.data);
      }

      if (birthdaysRes.success && birthdaysRes.data) {
        setUpcomingBirthdays(birthdaysRes.data);
      }

      if (healthRes.success && healthRes.data) {
        setBotHealth(healthRes.data);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching bot data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();

    // Refresh every 5 minutes
    const interval = setInterval(fetchAllData, 300000);

    return () => clearInterval(interval);
  }, []);

  const handleBirthdayCheck = async () => {
    setBirthdayCheckLoading(true);
    setBirthdayCheckMessage(null);

    const response = await discordApi.triggerBirthdayCheck();

    if (response.success && response.data) {
      const count = response.data.birthdaysToday?.length || 0;
      setBirthdayCheckMessage(
        count > 0
          ? `✅ Sent ${count} birthday notification(s)!`
          : '✅ No birthdays today'
      );
    } else {
      setBirthdayCheckMessage('❌ Failed to check birthdays');
    }

    setBirthdayCheckLoading(false);

    // Clear message after 3 seconds
    setTimeout(() => setBirthdayCheckMessage(null), 3000);
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatDate = (month: number, day: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[month - 1]} ${day}`;
  };

  return (
    <div className="min-h-screen p-8 lg:ml-64 lg:mr-80 bg-gradient-to-br from-[#f582ae] to-[#ffd803]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="mb-4 bg-white border-4 border-black px-6 py-3 font-black uppercase transition-all hover:translate-x-1 hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back
          </button>

          <h1 className="text-5xl md:text-7xl font-black mb-4 text-center">
            DISCORD BOT CONFIG
          </h1>

          {/* Bot Health Status */}
          {botHealth && (
            <div className="bg-[#b8f2e6] border-4 border-black p-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-center">
              <span className="font-black text-lg">
                🤖 Bot Status: {botHealth.status === 'healthy' ? '✅ ONLINE' : '❌ OFFLINE'}
              </span>
              <span className="font-bold text-sm ml-4">
                Uptime: {formatUptime(botHealth.uptime)}
              </span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white border-4 border-black p-12 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-center">
            <p className="text-2xl font-black">Loading bot data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-[#f582ae] border-4 border-black p-12 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-center">
            <p className="text-2xl font-black mb-2">⚠️ Connection Failed</p>
            <p className="text-lg font-bold">{error}</p>
            <button
              onClick={fetchAllData}
              className="mt-6 bg-white border-4 border-black px-8 py-3 font-black uppercase transition-all hover:translate-y-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
            >
              <FontAwesomeIcon icon={faRefresh} className="mr-2" />
              Retry
            </button>
          </div>
        )}

        {/* Main Content */}
        {!loading && !error && (
          <div className="space-y-6">
            {/* Server Stats */}
            {serverStats && (
              <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                <h2 className="text-3xl font-black mb-4 uppercase">
                  <FontAwesomeIcon icon={faUsers} className="mr-2" />
                  Server Statistics
                </h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#8bd3dd] border-4 border-black p-4 text-center">
                    <p className="text-4xl font-black">{serverStats.totalMembers}</p>
                    <p className="text-sm font-bold uppercase mt-1">Total Members</p>
                  </div>

                  <div className="bg-[#b8f2e6] border-4 border-black p-4 text-center">
                    <p className="text-4xl font-black text-green-600">{serverStats.onlineMembers}</p>
                    <p className="text-sm font-bold uppercase mt-1">Online</p>
                  </div>

                  <div className="bg-gray-200 border-4 border-black p-4 text-center">
                    <p className="text-4xl font-black text-gray-600">{serverStats.offlineMembers}</p>
                    <p className="text-sm font-bold uppercase mt-1">Offline</p>
                  </div>

                  <div className="bg-[#9b5de5] border-4 border-black p-4 text-center text-white">
                    <p className="text-4xl font-black">
                      <FontAwesomeIcon icon={faRocket} />
                      {' '}{serverStats.boostLevel}
                    </p>
                    <p className="text-sm font-bold uppercase mt-1">{serverStats.boosts} Boosts</p>
                  </div>
                </div>
              </div>
            )}

            {/* Voice Channels */}
            <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <h2 className="text-3xl font-black mb-4 uppercase">
                <FontAwesomeIcon icon={faMicrophone} className="mr-2" />
                Voice Channels
              </h2>

              {voiceChannels.length === 0 ? (
                <div className="bg-gray-100 border-4 border-black p-6 text-center">
                  <p className="font-bold">No active voice channels</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {voiceChannels.map((channel) => (
                    <div key={channel.id} className="bg-[#8bd3dd] border-4 border-black p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-black uppercase">{channel.name}</h3>
                        <span className="bg-white border-2 border-black px-3 py-1 font-black text-sm">
                          {channel.memberCount} {channel.memberCount === 1 ? 'member' : 'members'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {channel.members.map((member) => (
                          <div
                            key={member.id}
                            className="bg-white border-2 border-black p-2 flex items-center gap-2"
                          >
                            <img
                              src={discordApi.getAvatarUrl(member)}
                              alt={member.username}
                              className="w-8 h-8 rounded-full border-2 border-black"
                            />
                            <span className="font-bold text-sm truncate">
                              {member.displayName || member.username}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Birthdays */}
            <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-3xl font-black uppercase">
                  <FontAwesomeIcon icon={faCake} className="mr-2" />
                  Upcoming Birthdays
                </h2>

                <button
                  onClick={handleBirthdayCheck}
                  disabled={birthdayCheckLoading}
                  className="bg-[#ffd803] border-4 border-black px-4 py-2 font-black uppercase transition-all hover:translate-y-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {birthdayCheckLoading ? '...' : 'Check Now'}
                </button>
              </div>

              {birthdayCheckMessage && (
                <div className="mb-4 bg-[#b8f2e6] border-4 border-black p-3 text-center font-bold">
                  {birthdayCheckMessage}
                </div>
              )}

              {upcomingBirthdays.length === 0 ? (
                <div className="bg-gray-100 border-4 border-black p-6 text-center">
                  <p className="font-bold">No birthdays in the next 7 days</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingBirthdays.map((birthday, index) => (
                    <div
                      key={index}
                      className={`border-4 border-black p-4 ${
                        birthday.daysUntil === 0 ? 'bg-[#f582ae]' : 'bg-[#ffd803]'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-black">{birthday.name}</span>
                        <div className="text-right">
                          <div className="text-lg font-black">
                            {formatDate(birthday.month, birthday.day)}
                          </div>
                          <div className="text-sm font-bold">
                            {birthday.daysUntil === 0
                              ? 'TODAY! 🎉'
                              : `in ${birthday.daysUntil} day${birthday.daysUntil !== 1 ? 's' : ''}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Refresh Button */}
            <div className="text-center">
              <button
                onClick={fetchAllData}
                className="bg-[#9b5de5] text-white border-4 border-black px-8 py-4 font-black uppercase transition-all hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
              >
                <FontAwesomeIcon icon={faRefresh} className="mr-2" />
                Refresh Data
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BotConfig;
